import React, { useCallback, useEffect, useRef, useState } from 'react';
import './logo-scroller.css';

const DRAG_THRESHOLD = 8;
const RESUME_DELAY = 4200;
const AUTOPLAY_DELAY = 3000;
const SNAP_DEBOUNCE = 160;
const SNAP_SETTLE_DELAY = 460;

function LogoCard({ logo, clone, index, set }) {
  const content = <img src={logo.src} alt={clone ? '' : 'Logo de ' + logo.alt} />;

  return (
    <article
      className={'logo-scroller-item' + (clone ? ' logo-scroller-item--clone' : '')}
      data-logo-index={index}
      data-logo-set={set}
      aria-hidden={clone || undefined}
    >
      {logo.href ? (
        <a
          href={logo.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={clone ? undefined : logo.alt}
          tabIndex={clone ? -1 : undefined}
        >
          {content}
        </a>
      ) : content}
    </article>
  );
}

export default function LogoScroller({ logos = [], className = '', label = 'Patrocinadores principales' }) {
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const resumeAtRef = useRef(0);
  const snapTimerRef = useRef(null);
  const snappingRef = useRef(false);
  const currentIndexRef = useRef(0);
  const [dragging, setDragging] = useState(false);
  const many = logos.length > 1;

  const getCards = useCallback(() => {
    const viewport = viewportRef.current;
    return viewport ? Array.from(viewport.querySelectorAll('.logo-scroller-item')) : [];
  }, []);

  const getCardTarget = useCallback((card) => {
    const viewport = viewportRef.current;
    if (!viewport || !card) return 0;
    const viewportRect = viewport.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const paddingStart = Number.parseFloat(window.getComputedStyle(viewport).paddingLeft || '0') || 0;
    return viewport.scrollLeft + cardRect.left - viewportRect.left - paddingStart;
  }, []);

  const getNearestCard = useCallback(() => {
    const viewport = viewportRef.current;
    const cards = getCards();
    if (!viewport || !cards.length) return null;
    return cards.reduce((nearest, card) => (
      Math.abs(getCardTarget(card) - viewport.scrollLeft) < Math.abs(getCardTarget(nearest) - viewport.scrollLeft)
        ? card
        : nearest
    ), cards[0]);
  }, [getCardTarget, getCards]);

  const updateCurrentIndex = useCallback((card = getNearestCard()) => {
    if (!card) return;
    currentIndexRef.current = Number(card.dataset.logoIndex) || 0;
  }, [getNearestCard]);

  const getPeriod = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || !many) return 0;
    const gap = Number.parseFloat(window.getComputedStyle(viewport).columnGap || '0') || 0;
    return (viewport.scrollWidth + gap) / 3;
  }, [many]);

  const normaliseLoop = useCallback(() => {
    const viewport = viewportRef.current;
    const period = getPeriod();
    if (!viewport || !period) return 0;
    if (viewport.scrollLeft < period * 0.45) {
      viewport.scrollLeft += period;
      return period;
    }
    if (viewport.scrollLeft > period * 1.55) {
      viewport.scrollLeft -= period;
      return -period;
    }
    return 0;
  }, [getPeriod]);

  const pause = useCallback(() => {
    resumeAtRef.current = Date.now() + RESUME_DELAY;
  }, []);

  const preferredSnapBehavior = () => (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
  );

  const snapToCard = useCallback((card, behavior = 'smooth') => {
    const viewport = viewportRef.current;
    if (!viewport || !card) return;
    updateCurrentIndex(card);
    snappingRef.current = true;
    viewport.scrollTo({ left: getCardTarget(card), behavior });
    window.clearTimeout(snapTimerRef.current);
    snapTimerRef.current = window.setTimeout(() => {
      normaliseLoop();
      updateCurrentIndex();
      // Keep the internal loop rebase out of the manual-scroll debounce.
      window.setTimeout(() => { snappingRef.current = false; }, 32);
    }, behavior === 'smooth' ? SNAP_SETTLE_DELAY : 0);
  }, [getCardTarget, normaliseLoop, updateCurrentIndex]);

  const snapToNearest = useCallback((behavior) => {
    snapToCard(getNearestCard(), behavior || preferredSnapBehavior());
  }, [getNearestCard, snapToCard]);

  const scheduleSnap = useCallback(() => {
    window.clearTimeout(snapTimerRef.current);
    snapTimerRef.current = window.setTimeout(() => {
      if (!dragRef.current?.active && !snappingRef.current) snapToNearest();
    }, SNAP_DEBOUNCE);
  }, [snapToNearest]);

  const alignCentralCard = useCallback((index, behavior = 'auto') => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const card = viewport.querySelector('.logo-scroller-item[data-logo-set="1"][data-logo-index="' + index + '"]');
    if (card) snapToCard(card, behavior);
  }, [snapToCard]);

  useEffect(() => {
    if (!many) return undefined;
    const frame = window.requestAnimationFrame(() => alignCentralCard(0, 'auto'));
    return () => window.cancelAnimationFrame(frame);
  }, [alignCentralCard, logos.length, many]);

  useEffect(() => {
    if (!many || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = window.setInterval(() => {
      const cards = getCards();
      const current = getNearestCard();
      if (!cards.length || !current || dragging || Date.now() < resumeAtRef.current || snappingRef.current) return;
      const currentPosition = cards.indexOf(current);
      snapToCard(cards[currentPosition + 1] || cards[0]);
    }, AUTOPLAY_DELAY);
    return () => window.clearInterval(timer);
  }, [dragging, getCards, getNearestCard, logos.length, many, snapToCard]);

  useEffect(() => {
    if (!many) return undefined;
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    let frame = 0;
    const realign = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => alignCentralCard(currentIndexRef.current, 'auto'));
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(realign);
    observer?.observe(viewport);
    window.addEventListener('resize', realign);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', realign);
      window.cancelAnimationFrame(frame);
    };
  }, [alignCentralCard, many]);

  useEffect(() => () => window.clearTimeout(snapTimerRef.current), []);

  const handlePointerDown = (event) => {
    if (!many || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: viewport.scrollLeft,
      active: false,
      vertical: false,
    };
    pause();
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    const viewport = viewportRef.current;
    if (!drag || !viewport || drag.pointerId !== event.pointerId || drag.vertical) return;

    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (!drag.active) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < DRAG_THRESHOLD) return;
      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        drag.vertical = true;
        return;
      }
      drag.active = true;
      viewport.setPointerCapture?.(event.pointerId);
      setDragging(true);
    }

    event.preventDefault();
    viewport.scrollLeft = drag.startScrollLeft - deltaX;
    const loopAdjustment = normaliseLoop();
    if (loopAdjustment) drag.startScrollLeft += loopAdjustment;
    pause();
  };

  const finishPointer = (event) => {
    const drag = dragRef.current;
    const viewport = viewportRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.active) {
      suppressClickRef.current = true;
      window.setTimeout(() => { suppressClickRef.current = false; }, 180);
      viewport?.releasePointerCapture?.(event.pointerId);
      snapToNearest();
    }
    dragRef.current = null;
    setDragging(false);
    pause();
  };

  const handleScroll = () => {
    if (!dragRef.current?.active && !snappingRef.current) scheduleSnap();
  };

  const handleClickCapture = (event) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  };

  if (!logos.length) return null;
  const renderedLogos = many
    ? [0, 1, 2].flatMap((set) => logos.map((logo, index) => ({ logo, index, clone: set !== 1, set })))
    : logos.map((logo, index) => ({ logo, index, clone: false, set: 0 }));

  return (
    <section className={'logo-scroller ' + className} aria-label={label}>
      <div className="logo-scroller__header"><span>{label}</span></div>
      <div
        className={'logo-scroller-viewport ' + (many ? '' : 'logo-scroller-viewport--single') + (dragging ? ' logo-scroller-viewport--dragging' : '')}
        ref={viewportRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={finishPointer}
        onPointerEnter={pause}
        onFocus={pause}
        onScroll={handleScroll}
        onClickCapture={handleClickCapture}
      >
        {renderedLogos.map(({ logo, index, clone, set }) => (
          <LogoCard logo={logo} clone={clone} index={index} set={set} key={set + '-' + (logo.id || logo.src) + '-' + index} />
        ))}
      </div>
    </section>
  );
}
